import type {
  DevexCodexFeedbackItem,
  DevexCodexFeedbackPullRequestState,
  DevexCodexFeedbackReactionFilter,
  DevexCodexFeedbackRejectionReason,
  DevexCodexFeedbackReplySentiment,
  DevexCodexFeedbackSentimentPoint,
  DevexCodexFeedbackSentimentSummary,
} from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type { LegendProps, TooltipProps } from 'recharts';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { formatChartDate, formatCount } from '@/components/devex-metrics/review-latency-formatters';
import { EmptyState, ErrorBanner, SkeletonBlock } from '@/components/ui/state-views';
import { getCodexFeedback } from '@/config/api-helpers';

const STATE_LABELS: Record<DevexCodexFeedbackPullRequestState, string> = {
  CLOSED: 'Closed',
  MERGED: 'Merged',
  OPEN: 'Open',
};
const COLOR_HELPFUL = '#417428';
const COLOR_NOT_HELPFUL = '#9B4728';
const COLOR_UNRATED = '#CBD5E1';
const COLOR_VOLUME = '#285276';
const COMMENTS_PAGE_SIZE = 25;
const REPLY_SENTIMENT_LABELS: Record<DevexCodexFeedbackReplySentiment, string> = {
  accepted: 'Accepted',
  mixed: 'Mixed',
  question: 'Question',
  rejected: 'Rejected',
  unknown: 'Unknown',
};
const REJECTION_REASON_LABELS: Record<DevexCodexFeedbackRejectionReason, string> = {
  alreadyHandled: 'Already handled',
  deferredOrOutOfScope: 'Deferred/out of scope',
  incorrectOrStale: 'Incorrect/stale',
  intentionalOrFutureWork: 'Intentional/future work',
  localOrOneOff: 'Local/one-off',
  lowRiskOrNotNeeded: 'Low risk/not needed',
  other: 'Other',
};
const REPLY_SENTIMENT_STYLES: Record<DevexCodexFeedbackReplySentiment, string> = {
  accepted: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  mixed: 'border-amber-200 bg-amber-50 text-amber-700',
  question: 'border-sky-200 bg-sky-50 text-sky-700',
  rejected: 'border-red-200 bg-red-50 text-red-700',
  unknown: 'border-neutral-200 bg-neutral-50 text-neutral-600',
};

const SENTIMENT_LEGEND = [
  { color: COLOR_HELPFUL, dataKey: 'helpfulPct', label: 'Accepted' },
  { color: COLOR_NOT_HELPFUL, dataKey: 'notHelpfulPct', label: 'Rejected' },
  { color: COLOR_UNRATED, dataKey: 'unratedPct', label: 'Unclassified' },
  { color: COLOR_VOLUME, dataKey: 'totalComments', label: 'Comments' },
];

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString('en-US', {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    timeZoneName: 'short',
  });
}

function formatThreadLocation(item: DevexCodexFeedbackItem): string {
  const line = item.thread.line ?? item.thread.startLine;
  return line != null ? `${item.thread.path}:${line}` : item.thread.path || 'Unknown file';
}

function formatPercent(value: number): string {
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value)}%`;
}

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <div className='rounded-md border border-neutral-200 bg-white px-3 py-2.5'>
      <div className='text-xs text-neutral-500'>{label}</div>
      <div className='font-mediumbold mt-1 text-xl text-neutral-900'>{value.toLocaleString()}</div>
    </div>
  );
}

function CodexFeedbackSkeleton() {
  return (
    <div className='space-y-4' aria-label='Loading Codex feedback'>
      <div className='rounded-lg border border-neutral-200 bg-white'>
        <div className='space-y-3 border-b border-neutral-200 px-4 py-3 sm:px-6'>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <SkeletonBlock className='h-4 w-44' />
            <SkeletonBlock className='h-6 w-28 rounded-full' />
          </div>
          <div className='flex flex-wrap gap-2'>
            <SkeletonBlock className='h-7 w-32 rounded-full' />
            <SkeletonBlock className='h-7 w-32 rounded-full' />
            <SkeletonBlock className='h-7 w-36 rounded-full' />
          </div>
        </div>
        <div className='space-y-4 p-4 sm:p-6'>
          <SkeletonBlock className='h-56 w-full' />
          <div className='flex justify-center'>
            <SkeletonBlock className='h-10 w-80 max-w-full' />
          </div>
        </div>
      </div>
      <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
        <SkeletonBlock className='h-20' />
        <SkeletonBlock className='h-20' />
        <SkeletonBlock className='h-20' />
      </div>
      <div className='space-y-3'>
        <SkeletonBlock className='h-11' />
        <SkeletonBlock className='h-36' />
        <SkeletonBlock className='h-36' />
      </div>
    </div>
  );
}

function SentimentLegend({ payload }: LegendProps) {
  const entries = payload?.length
    ? payload.map((entry) => ({
        color: String(entry.color ?? COLOR_UNRATED),
        value: String(entry.value),
      }))
    : SENTIMENT_LEGEND.map((entry) => ({ color: entry.color, value: entry.label }));

  return (
    <div className='mt-3 flex justify-center'>
      <div className='inline-flex flex-wrap justify-center gap-x-4 gap-y-2 rounded-lg border border-neutral-100 bg-white/50 px-4 py-2.5'>
        {entries.map((entry) => (
          <span key={entry.value} className='inline-flex items-center gap-1.5 text-sm text-neutral-600'>
            <span className='size-2.5 shrink-0 rounded-full' style={{ backgroundColor: entry.color }} />
            {entry.value}
          </span>
        ))}
      </div>
    </div>
  );
}

function SentimentTooltip({ active, label, payload }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload as DevexCodexFeedbackSentimentPoint | undefined;
  if (!point) return null;

  const rows = [
    {
      color: COLOR_HELPFUL,
      count: point.helpfulCount,
      label: 'Accepted',
      percent: point.helpfulPct,
    },
    {
      color: COLOR_NOT_HELPFUL,
      count: point.notHelpfulCount,
      label: 'Rejected',
      percent: point.notHelpfulPct,
    },
    {
      color: COLOR_UNRATED,
      count: point.unratedCount,
      label: 'Unclassified',
      percent: point.unratedPct,
    },
  ];

  return (
    <div className='rounded-lg border border-neutral-200 bg-white px-3 py-2.5 shadow-lg'>
      <p className='font-mediumbold mb-2 text-xs text-neutral-600'>{formatChartDate(String(label))}</p>
      <div className='space-y-1'>
        <div className='flex items-center gap-2 text-xs'>
          <span className='size-2 shrink-0 rounded-full' style={{ backgroundColor: COLOR_VOLUME }} />
          <span className='text-neutral-500'>Comments</span>
          <span className='font-mediumbold ml-auto pl-4 text-neutral-900'>{formatCount(point.totalComments)}</span>
        </div>
        {rows.map((row) => (
          <div key={row.label} className='flex items-center gap-2 text-xs'>
            <span className='size-2 shrink-0 rounded-full' style={{ backgroundColor: row.color }} />
            <span className='text-neutral-500'>{row.label}</span>
            <span className='font-mediumbold ml-auto pl-4 text-neutral-900'>
              {formatPercent(row.percent)} ({formatCount(row.count)})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SentimentPill({
  color,
  count,
  label,
  percent,
}: {
  color: string;
  count: number;
  label: string;
  percent: number;
}) {
  return (
    <span className='inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-xs text-neutral-600'>
      <span className='size-2 shrink-0 rounded-full' style={{ backgroundColor: color }} />
      <span className='font-mediumbold text-neutral-800'>{formatPercent(percent)}</span>
      {label}
      <span className='text-neutral-400'>({formatCount(count)})</span>
    </span>
  );
}

function SentimentChart({
  summary,
  trendData,
}: {
  summary: DevexCodexFeedbackSentimentSummary;
  trendData: DevexCodexFeedbackSentimentPoint[];
}) {
  const total = summary.totalComments;
  const helpfulPct = total > 0 ? (summary.helpfulCount / total) * 100 : 0;
  const notHelpfulPct = total > 0 ? (summary.notHelpfulCount / total) * 100 : 0;
  const unratedPct = total > 0 ? (summary.unratedCount / total) * 100 : 0;

  return (
    <div className='rounded-lg border border-neutral-200 bg-white'>
      <div className='space-y-3 border-b border-neutral-200 px-4 py-3 sm:px-6'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <h3 className='font-mediumbold text-sm text-neutral-900'>Top-Line Codex Feedback</h3>
          <span className='rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-xs text-neutral-600'>
            {formatCount(total)} comments
          </span>
        </div>
        <div className='flex flex-wrap gap-2'>
          <SentimentPill color={COLOR_HELPFUL} count={summary.helpfulCount} label='accepted' percent={helpfulPct} />
          <SentimentPill
            color={COLOR_NOT_HELPFUL}
            count={summary.notHelpfulCount}
            label='rejected'
            percent={notHelpfulPct}
          />
          <SentimentPill color={COLOR_UNRATED} count={summary.unratedCount} label='unclassified' percent={unratedPct} />
        </div>
      </div>
      <div className='p-4 sm:p-6'>
        <ResponsiveContainer width='100%' height={260}>
          <ComposedChart data={trendData} margin={{ top: 12, right: 8, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray='3 3' stroke='#f1f5f9' vertical={false} />
            <XAxis
              dataKey='date'
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              tickFormatter={formatChartDate}
              tickLine={false}
              axisLine={false}
              minTickGap={28}
            />
            <YAxis
              yAxisId='percent'
              domain={[0, 100]}
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              tickFormatter={(value) => `${value}%`}
              tickLine={false}
              axisLine={false}
              width={42}
            />
            <YAxis
              yAxisId='volume'
              orientation='right'
              tick={{ fontSize: 10, fill: '#CBD5E1' }}
              tickFormatter={formatCount}
              tickLine={false}
              axisLine={false}
              width={36}
              allowDecimals={false}
            />
            <Tooltip content={<SentimentTooltip />} />
            <Legend content={<SentimentLegend />} />
            <Bar yAxisId='percent' dataKey='helpfulPct' name='Accepted' stackId='sentiment' fill={COLOR_HELPFUL} />
            <Bar
              yAxisId='percent'
              dataKey='notHelpfulPct'
              name='Rejected'
              stackId='sentiment'
              fill={COLOR_NOT_HELPFUL}
            />
            <Bar
              yAxisId='percent'
              dataKey='unratedPct'
              name='Unclassified'
              stackId='sentiment'
              fill={COLOR_UNRATED}
              radius={[4, 4, 0, 0]}
            />
            <Line
              yAxisId='volume'
              type='monotone'
              dataKey='totalComments'
              name='Comments'
              stroke={COLOR_VOLUME}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ReplySentimentBadge({
  rejectionReason,
  sentiment,
}: {
  rejectionReason: DevexCodexFeedbackRejectionReason | null;
  sentiment: DevexCodexFeedbackReplySentiment;
}) {
  const reasonLabel = rejectionReason ? REJECTION_REASON_LABELS[rejectionReason] : null;

  return (
    <span
      className={`rounded-full border px-2 py-0.5 text-xs ${REPLY_SENTIMENT_STYLES[sentiment]}`}
      title={reasonLabel ? `${REPLY_SENTIMENT_LABELS[sentiment]}: ${reasonLabel}` : REPLY_SENTIMENT_LABELS[sentiment]}
    >
      {REPLY_SENTIMENT_LABELS[sentiment]}
      {reasonLabel ? `: ${reasonLabel}` : ''}
    </span>
  );
}

function getPageNumbers(currentPage: number, pageCount: number): number[] {
  const start = Math.max(1, currentPage - 2);
  const end = Math.min(pageCount, currentPage + 2);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

function PaginationControls({
  currentPage,
  itemCount,
  onPageChange,
  pageCount,
}: {
  currentPage: number;
  itemCount: number;
  onPageChange: (page: number) => void;
  pageCount: number;
}) {
  if (pageCount <= 1) {
    return null;
  }

  const firstItem = (currentPage - 1) * COMMENTS_PAGE_SIZE + 1;
  const lastItem = Math.min(itemCount, currentPage * COMMENTS_PAGE_SIZE);
  const pageNumbers = getPageNumbers(currentPage, pageCount);

  return (
    <div className='flex flex-wrap items-center justify-between gap-3 rounded-md border border-neutral-200 bg-white px-3 py-2'>
      <span className='text-xs text-neutral-500'>
        Showing {formatCount(firstItem)}-{formatCount(lastItem)} of {formatCount(itemCount)}
      </span>
      <div className='flex flex-wrap items-center gap-1'>
        <button
          type='button'
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className='rounded border border-neutral-200 px-2.5 py-1 text-xs text-neutral-700 disabled:cursor-not-allowed disabled:opacity-40'
        >
          Previous
        </button>
        {pageNumbers[0] !== 1 && (
          <>
            <button
              type='button'
              onClick={() => onPageChange(1)}
              className='rounded border border-neutral-200 px-2.5 py-1 text-xs text-neutral-700'
            >
              1
            </button>
            <span className='px-1 text-xs text-neutral-400'>...</span>
          </>
        )}
        {pageNumbers.map((page) => (
          <button
            key={page}
            type='button'
            onClick={() => onPageChange(page)}
            className={`rounded border px-2.5 py-1 text-xs ${
              page === currentPage
                ? 'border-neutral-900 bg-neutral-900 text-white'
                : 'border-neutral-200 text-neutral-700'
            }`}
          >
            {page}
          </button>
        ))}
        {pageNumbers[pageNumbers.length - 1] !== pageCount && (
          <>
            <span className='px-1 text-xs text-neutral-400'>...</span>
            <button
              type='button'
              onClick={() => onPageChange(pageCount)}
              className='rounded border border-neutral-200 px-2.5 py-1 text-xs text-neutral-700'
            >
              {pageCount}
            </button>
          </>
        )}
        <button
          type='button'
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === pageCount}
          className='rounded border border-neutral-200 px-2.5 py-1 text-xs text-neutral-700 disabled:cursor-not-allowed disabled:opacity-40'
        >
          Next
        </button>
      </div>
    </div>
  );
}

function CommentBlock({ body }: { body: string }) {
  return (
    <pre className='max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-md border border-neutral-200 bg-neutral-50 p-3 font-mono text-xs leading-relaxed text-neutral-800'>
      {body}
    </pre>
  );
}

function ReactionBadge({
  ariaLabel,
  emoji,
  tone,
  value,
}: {
  ariaLabel: string;
  emoji: string;
  tone: 'negative' | 'neutral';
  value: number;
}) {
  const className =
    tone === 'negative' && value > 0
      ? 'border-red-200 bg-red-50 text-red-700'
      : 'border-neutral-200 bg-neutral-50 text-neutral-600';

  return (
    <span className={`rounded-full border px-2 py-0.5 text-xs ${className}`} title={ariaLabel} aria-label={ariaLabel}>
      {emoji} {value.toLocaleString()}
    </span>
  );
}

function FeedbackItem({ item }: { item: DevexCodexFeedbackItem }) {
  const threadState = [
    item.thread.isResolved ? 'Resolved' : 'Unresolved',
    item.thread.isOutdated ? 'Outdated' : null,
    item.thread.commentsTruncated ? '100+ comments' : null,
  ].filter(Boolean);
  const codexReactions = item.codexComment.reactions;

  return (
    <details className='rounded-md border border-neutral-200 bg-white p-4 open:border-neutral-300'>
      <summary className='cursor-pointer list-none'>
        <div className='flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between'>
          <div className='min-w-0'>
            <div className='flex flex-wrap items-center gap-2 text-xs text-neutral-500'>
              <span>#{item.pullRequest.number}</span>
              <span>{STATE_LABELS[item.pullRequest.state]}</span>
              <span>{formatThreadLocation(item)}</span>
            </div>
            <h3 className='font-mediumbold mt-1 break-words text-sm text-neutral-900'>{item.pullRequest.title}</h3>
            <div className='mt-1 text-xs text-neutral-500'>
              @{item.reply.authorLogin || 'unknown'} replied {formatDateTime(item.reply.createdAt)}
            </div>
          </div>
          <div className='flex shrink-0 flex-wrap items-center gap-2'>
            <ReplySentimentBadge rejectionReason={item.rejectionReason} sentiment={item.replySentiment} />
            {codexReactions.thumbsUp > 0 && (
              <ReactionBadge
                ariaLabel='Codex thumbs up reactions'
                emoji='👍'
                tone='neutral'
                value={codexReactions.thumbsUp}
              />
            )}
            {codexReactions.thumbsDown > 0 && (
              <ReactionBadge
                ariaLabel='Codex thumbs down reactions'
                emoji='👎'
                tone='negative'
                value={codexReactions.thumbsDown}
              />
            )}
            {threadState.map((state) => (
              <span
                key={state}
                className='rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-xs text-neutral-600'
              >
                {state}
              </span>
            ))}
            <a
              href={item.reply.url || item.pullRequest.url}
              target='_blank'
              rel='noreferrer'
              className='rounded-md border border-neutral-200 px-2.5 py-1 text-xs text-neutral-700 hover:border-neutral-300'
              onClick={(event) => event.stopPropagation()}
            >
              GitHub
            </a>
          </div>
        </div>
      </summary>

      <div className='mt-4 grid gap-4 lg:grid-cols-2'>
        <div>
          <div className='mb-2 flex items-center justify-between gap-2'>
            <h4 className='font-mediumbold text-xs uppercase text-neutral-500'>Codex comment</h4>
            <span className='text-xs text-neutral-400'>{formatDateTime(item.codexComment.createdAt)}</span>
          </div>
          <CommentBlock body={item.codexComment.body} />
        </div>
        <div>
          <div className='mb-2 flex items-center justify-between gap-2'>
            <h4 className='font-mediumbold text-xs uppercase text-neutral-500'>Engineer reply</h4>
            <span className='text-xs text-neutral-400'>{formatDateTime(item.reply.createdAt)}</span>
          </div>
          <CommentBlock body={item.reply.body} />
        </div>
      </div>
    </details>
  );
}

export function CodexFeedbackView({
  botLogins,
  days,
  includeBotReplies,
  prNumbers,
  reactionFilter,
  states,
}: {
  botLogins: string;
  days: number;
  includeBotReplies: boolean;
  prNumbers: string;
  reactionFilter: DevexCodexFeedbackReactionFilter;
  states: DevexCodexFeedbackPullRequestState[];
}) {
  const [commentPage, setCommentPage] = useState(1);
  const query = useQuery({
    queryKey: ['codex-feedback', days, states, prNumbers, botLogins, includeBotReplies, reactionFilter],
    queryFn: () =>
      getCodexFeedback({
        botLogins,
        days,
        includeBotReplies,
        prNumbers,
        reactionFilter,
        states,
      }),
    staleTime: 5 * 60 * 1000,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data?.items]);
  const pullRequestCount = query.data?.pullRequestCount ?? 0;
  const replyCount = query.data?.replyCount ?? 0;
  const scannedPullRequestCount = query.data?.scannedPullRequestCount ?? 0;
  const botLoginText = useMemo(() => query.data?.filters.botLogins.join(', ') ?? '', [query.data?.filters.botLogins]);
  const sentiment = query.data?.sentiment;
  const commentPageCount = Math.max(1, Math.ceil(items.length / COMMENTS_PAGE_SIZE));
  const resolvedCommentPage = Math.min(commentPage, commentPageCount);
  const paginatedItems = useMemo(() => {
    const start = (resolvedCommentPage - 1) * COMMENTS_PAGE_SIZE;
    return items.slice(start, start + COMMENTS_PAGE_SIZE);
  }, [items, resolvedCommentPage]);

  useEffect(() => {
    setCommentPage(1);
  }, [botLogins, days, includeBotReplies, prNumbers, reactionFilter, states]);

  useEffect(() => {
    if (commentPage > commentPageCount) {
      setCommentPage(commentPageCount);
    }
  }, [commentPage, commentPageCount]);

  if (query.isLoading) {
    return <CodexFeedbackSkeleton />;
  }

  if (query.error) {
    const message = query.error instanceof Error ? query.error.message : 'Unknown error';

    return (
      <ErrorBanner
        message={message}
        onRetry={() => {
          void query.refetch();
        }}
        title='Codex feedback is unavailable'
        className='sticky top-3 z-20 mx-0 my-4 shadow-sm'
      />
    );
  }

  return (
    <div className='space-y-4'>
      {sentiment && sentiment.summary.totalComments > 0 && (
        <SentimentChart summary={sentiment.summary} trendData={sentiment.points} />
      )}

      <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
        <SummaryStat label='Engineer replies' value={replyCount} />
        <SummaryStat label='PRs with replies' value={pullRequestCount} />
        <SummaryStat label='PRs represented' value={scannedPullRequestCount} />
      </div>

      {botLoginText && (
        <div className='rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-600'>
          Bot logins: <span className='font-mediumbold text-neutral-800'>{botLoginText}</span>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState message='No Codex feedback replies found for these filters.' />
      ) : (
        <div className='space-y-3'>
          <PaginationControls
            currentPage={resolvedCommentPage}
            itemCount={items.length}
            onPageChange={setCommentPage}
            pageCount={commentPageCount}
          />
          {paginatedItems.map((item) => (
            <FeedbackItem key={`${item.thread.id}:${item.reply.url}`} item={item} />
          ))}
          <PaginationControls
            currentPage={resolvedCommentPage}
            itemCount={items.length}
            onPageChange={setCommentPage}
            pageCount={commentPageCount}
          />
        </div>
      )}
    </div>
  );
}
